import { createFuzzySearchIndex } from "app/pages/sales-intelligence/services/fuzzy-search/factory";
import { normalizeForSearch } from "app/pages/sales-intelligence/services/fuzzy-search/normalize";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type { FilterCategory } from "../../types/category";
import type { SearchFilterInstance } from "../../types/searchFilterInstance";

import { FUSE_OPTIONS, LONG_QUERY_THRESHOLD, MAX_SEARCH_FILTERS_RESULTS, SHORT_QUERY_THRESHOLD } from "./constants";
import type {
    FilterSearchConfigMap,
    IFilterSearchService,
    ISearchDoc,
    FiltersSearchParams,
    FiltersSearchResult,
} from "./types";

type FactoryOptions<K extends string> = {
    searchConfigMaps: readonly FilterSearchConfigMap<K>[];
    getCategoryI18nKey: (category: FilterCategory) => string;
};

type DocSource = readonly SearchFilterInstance[];

const getScoreThresholdForQuery = (queryLength: number): number =>
    queryLength <= 3 ? SHORT_QUERY_THRESHOLD : LONG_QUERY_THRESHOLD;

export const createFilterSearchService = <K extends string>(options: FactoryOptions<K>): IFilterSearchService<K> => {
    const { searchConfigMaps, getCategoryI18nKey } = options;

    const findConfig = (key: K): FilterSearchConfigMap[K] | null => {
        for (const map of searchConfigMaps) {
            const config = map[key];

            if (config) {
                return config;
            }
        }

        return null;
    };

    const getSearchGroups = (
        filter: SearchFilterInstance,
        translateFn: (key: string) => string,
    ): readonly string[] | null => {
        if (!filter.category) {
            return null;
        }

        const categories: FilterCategory[] = Array.isArray(filter.category) ? filter.category : [filter.category];

        return categories.map((cat) => translateFn(getCategoryI18nKey(cat)));
    };

    const getSearchKeywords = (
        filter: SearchFilterInstance,
        translateFn: (key: string) => string | string[],
    ): readonly string[] | null => {
        const { translationKeys, literals } = findConfig(filter.key as K)?.keywords ?? {};

        const result = [...(translationKeys?.map(translateFn) ?? []), ...(literals ?? [])].flat();

        return result.length > 0 ? result : null;
    };

    const getSearchFilterLabel = (filter: SearchFilterInstance, translateFn: (key: string) => string) => {
        if (is.string(filter.title)) {
            return filter.title;
        }

        if (filter.title?.key) {
            return translateFn(filter.title.key);
        }

        return "";
    };

    // Read at rebuild time rather than captured in the source, so the docs follow the current locale's
    // translations. The index caches on (filters, locale); translateFn is not part of that key.
    let currentTranslateFn: (key: string) => string = (key) => key;

    const buildDocs = (filters: DocSource, locale: string): ISearchDoc<K>[] =>
        filters.map((filter) => {
            const label = getSearchFilterLabel(filter, currentTranslateFn);
            const groups = getSearchGroups(filter, currentTranslateFn);
            const keywords = getSearchKeywords(filter, currentTranslateFn);

            return {
                id: filter.key as K,
                label: normalizeForSearch(label, locale),
                groups: groups?.map((group) => normalizeForSearch(group, locale)),
                keywords: keywords?.map((keyword) => normalizeForSearch(keyword, locale)),
            };
        });

    const index = createFuzzySearchIndex<DocSource, K, ISearchDoc<K>>({
        buildDocs,
        fuseOptions: FUSE_OPTIONS,
        scoreThresholdFor: getScoreThresholdForQuery,
        limit: MAX_SEARCH_FILTERS_RESULTS,
    });

    const search = ({ filters, query, locale, translateFn }: FiltersSearchParams): FiltersSearchResult<K> => {
        if (!query.trim()) {
            return { matchedIds: [], autoExpandFilterKey: null };
        }

        currentTranslateFn = translateFn;

        const hits = index.search({ source: filters, query, locale });
        const matchedIds = hits.map((hit) => hit.id);
        const autoExpandFilterKey = matchedIds.find((id) => findConfig(id)?.autoExpandOnSearchMatch) ?? null;

        return { matchedIds, autoExpandFilterKey };
    };

    const invalidateCache = (): void => {
        index.invalidate();
    };

    return {
        search,
        invalidateCache,
    };
};
